import APIS_CONSTANTS from "./ApiConstants";
import http from "./http";

class AuthServices {
  // A rejected sign-in is answered 401. That is this request's normal failure,
  // not an expired session, so it must not clear the visitor's conversation —
  // see the 401 handling in Services/http.
  login(userData) {
    return http.post(APIS_CONSTANTS.login, userData, {
      expectedUnauthorized: true,
    });
  }
  register(userData) {
    return http.post(APIS_CONSTANTS.register, userData);
  }
  logout() {
    return http.post(APIS_CONSTANTS.logout);
  }
  getUserInfo() {
    return http.get(APIS_CONSTANTS.info);
  }
  // Silent: a failure here means "sign in instead", which the caller says in
  // its own words; the generic error toast would only confuse a visitor.
  //
  // `guestKey` is the secret from this browser's previous visit, when it has
  // one (see Utils/guestKey.js). Left out entirely when there is none.
  //
  // `visitorId` is the device id the shop's own tracker already uses, handed in
  // by the SDK (see Utils/embedVisitor.js). It is what lets the backend join
  // this guest to the browsing it did before it ever spoke to the agent.
  // Without it a guest's page views and their conversation describe two
  // different devices and can never be put together.
  createGuestSession(orgId, guestKey, visitorId) {
    const body = { organizationId: Number(orgId) };
    if (guestKey) body.guestKey = guestKey;
    if (visitorId) body.visitorId = visitorId;
    return http.post(APIS_CONSTANTS.guestSession, body, { silentError: true });
  }
  // The handover: the conversation this visitor had as a guest, and everything
  // the shop learned about them in it, moves onto the account they have just
  // created. The backend has done this since June and nothing ever called it.
  //
  // `guestToken` is the proof. Belonging to the same brand is not evidence that
  // an anonymous conversation is yours — every customer and every member of
  // staff belongs to it — so the backend refuses without the token minted for
  // that guest alone. Authorization carries the new account's token, which is
  // how the backend knows who to hand the conversation to.
  //
  // Silent: a visitor who has just signed up must never be shown an error about
  // a transfer they did not ask for. Losing the history is bad; losing the
  // account because the transfer failed is worse.
  //
  // Bounded for the same reason: this call is awaited before the chat loads,
  // so it must settle. A slow or hung transfer failing is the history staying
  // where it was; a hung transfer hanging the sign-in is the customer lost.
  mergeGuestSession({ guestSessionId, guestToken }) {
    return http.post(
      APIS_CONSTANTS.guestMerge,
      { guestSessionId, guestToken },
      { silentError: true, timeout: 10000 }
    );
  }
  recoverPassword({ email, org_id }) {
    return http.get(APIS_CONSTANTS.recover, {
      params: { email, org_id, employee: false, activation_method: "OTP" },
    });
  }
  otpVerify({ email, org_id, otp }) {
    return http.post(APIS_CONSTANTS.otpVerify, {
      email,
      org_id,
      employee: false,
      otp,
    });
  }
  otpActivate({ email, org_id, otp, password }) {
    return http.post(APIS_CONSTANTS.otpActivate, {
      email,
      orgId: org_id,
      otp,
      password,
    });
  }

  resendOtp({ email, org_id }) {
    return http.post(APIS_CONSTANTS.resendOtp, {
      email,
      org_id,
      activation_method: "OTP",
    });
  }

  newPassword({ token, password, org_id }) {
    return http.post(APIS_CONSTANTS.newPassword, {
      token,
      password,
      org_id,
      employee: false,
    });
  }

  getChatByUserId(userId) {
    const url = `${APIS_CONSTANTS.getChatByUserId}/${userId}`;
    return http.get(url);
  }

  uploadUserAvatar(imageData, token) {
    return http.post(APIS_CONSTANTS.uploadUserAvatar, imageData, {
      headers: {
        'User-Token': token,
        'Content-Type': 'application/json'
      }
    });
  }

  updateUser(data) {
    return http.post(APIS_CONSTANTS.updateUser, data);
  }
}

export default new AuthServices();
